import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Select from '@components/V4/Select/Select';
import TriStateToggle from '@components/V4/TriStateToggle/TriStateToggle';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline';
import {
  EmptyState,
  FilterField,
  SectionHeading,
  TableShell,
} from '@modules/Admin/Reporting/common';
import { CourseMentorDTO } from '@shared/apis/courses.apis';
import { TriState } from '@shared/clientTypes/misc';
import { ENROLLMENT_STATUS_TYPES } from '@shared/constants';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import { Fragment, useMemo, useState } from 'react';

const headRow = 'border-b border-white/10 text-xs uppercase tracking-wide text-customGrey';
const bodyRow = 'border-b border-white/5 align-top';
const leftHead = 'px-4 py-2.5 text-left font-semibold';
const rightHead = 'px-4 py-2.5 text-right font-semibold';
const leftCell = 'px-4 py-2.5 text-left text-customLight';
const rightCell = 'px-4 py-2.5 text-right tabular-nums text-customLight';

const yearBreakdown = (students: CourseMentorDTO['students']) => {
  const counts = new Map<string, number>();
  students.forEach((student) => {
    const key = student.graduationYear ? String(student.graduationYear) : 'no year';
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b));
};

const CourseMentors = (props: { mentors?: CourseMentorDTO[]; isLoading: boolean }) => {
  const { mentors, isLoading } = props;
  const [mentorId, setMentorId] = useState<string>();
  const [year, setYear] = useState<string>();
  const [activeOnly, setActiveOnly] = useState<TriState>('checked');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const years = useMemo(
    () =>
      [
        ...new Set(
          (mentors ?? []).flatMap((mentor) =>
            mentor.students.map((student) => student.graduationYear).filter(Boolean)
          )
        ),
      ].sort() as number[],
    [mentors]
  );

  const rows = useMemo(
    () =>
      (mentors ?? [])
        .filter((mentor) => !mentorId || mentor._id === mentorId)
        .map((mentor) => ({
          mentor,
          students: mentor.students.filter(
            (student) =>
              (activeOnly === 'indeterminate' || student.isActive === (activeOnly === 'checked')) &&
              (!year || String(student.graduationYear) === year)
          ),
        }))
        .filter((row) => row.students.length),
    [mentors, mentorId, year, activeOnly]
  );

  const totalStudents = rows.reduce((sum, row) => sum + row.students.length, 0);

  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <LoadingSpinner size="normal" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SectionHeading
        title={`Mentors on this course · ${rows.length} mentor${
          rows.length === 1 ? '' : 's'
        }, ${totalStudents} student${totalStudents === 1 ? '' : 's'}`}
      />
      <div className="flex flex-wrap items-end gap-4">
        <FilterField width="w-full tablet:w-64">
          <Select
            label="Mentor"
            variant="secondary"
            showSearch
            allowClear
            value={mentorId}
            options={(mentors ?? []).map((mentor) => ({
              value: mentor._id,
              label: `${mentor.fullName} (${
                mentor.students.filter((s) => s.isActive).length
              } active)`,
            }))}
            onSelect={(value) => setMentorId(value ? String(value) : undefined)}
            onClear={() => setMentorId(undefined)}
            placeholder="All mentors"
          />
        </FilterField>
        <FilterField width="w-full tablet:w-44">
          <Select
            label="Graduation year"
            variant="secondary"
            allowClear
            value={year}
            options={years.map((value) => ({ value: String(value), label: String(value) }))}
            onSelect={(value) => setYear(value ? String(value) : undefined)}
            onClear={() => setYear(undefined)}
            placeholder="Any year"
          />
        </FilterField>
        <TriStateToggle
          title="Enrollments:"
          options={[
            { label: 'Active', value: 'checked' },
            { label: 'Ended', value: 'unchecked' },
            { label: 'All', value: 'indeterminate' },
          ]}
          value={activeOnly}
          onChange={setActiveOnly}
        />
      </div>

      {!rows.length ? (
        <EmptyState>No mentors match these filters.</EmptyState>
      ) : (
        <TableShell minWidth="min-w-[720px]">
          <thead>
            <tr className={headRow}>
              <th className={leftHead}>Mentor</th>
              <th className={rightHead}>Students</th>
              <th className={leftHead}>By graduation year</th>
              <th className="w-10 px-2 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {rows.map(({ mentor, students }) => {
              const isOpen = expanded.has(mentor._id);
              return (
                <Fragment key={mentor._id}>
                  <tr
                    className={mergeClassNames(bodyRow, 'cursor-pointer hover:bg-white/5')}
                    onClick={() => toggle(mentor._id)}
                  >
                    <td className={leftCell}>
                      <span className="font-semibold text-white">{mentor.fullName}</span>
                      {mentor.status ? (
                        <span className="ml-2 text-xs text-customGrey">{mentor.status}</span>
                      ) : null}
                    </td>
                    <td className={mergeClassNames(rightCell, 'font-semibold text-white')}>
                      {students.length}
                    </td>
                    <td className={leftCell}>
                      <span className="flex flex-wrap gap-1.5">
                        {yearBreakdown(students).map(([label, count]) => (
                          <span
                            key={label}
                            className="rounded-full bg-white/10 px-2 py-0.5 text-xs text-customLight"
                          >
                            {label} × {count}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td className="px-2 py-2.5 text-customGrey">
                      {isOpen ? (
                        <ChevronUpIcon className="h-4 w-4" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </td>
                  </tr>
                  {isOpen ? (
                    <tr className={bodyRow}>
                      <td colSpan={4} className="bg-black/20 px-6 py-3">
                        <table className="w-full text-sm">
                          <tbody>
                            {students.map((student) => (
                              <tr
                                key={student.enrollmentId}
                                className="border-b border-white/5 last:border-b-0"
                              >
                                <td className="py-1.5 pr-4 text-customLight">{student.fullName}</td>
                                <td className="py-1.5 pr-4 tabular-nums text-customLight">
                                  {student.graduationYear ?? '—'}
                                </td>
                                <td className="py-1.5 pr-4 text-xs text-customGrey">
                                  {student.enrollmentStatus !== undefined
                                    ? ENROLLMENT_STATUS_TYPES[student.enrollmentStatus]
                                    : '—'}
                                </td>
                                <td className="py-1.5 text-right tabular-nums text-xs text-customGrey">
                                  {student.sessionsDone} / {student.sessionsTotal} sessions
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </TableShell>
      )}
    </div>
  );
};

export default CourseMentors;
